import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { authenticatedFetch } from '../utils/api';

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(208, 215, 222, 0.8);
`;

const RecordsContainer = styled.div`
  max-height: calc(4 * 40px);
  overflow-y: auto;
  padding-right: 8px;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const Title = styled.div`
  color: #333333;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 0.85rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const Label = styled.span`
  color: #333333;
  font-weight: 600;
  min-width: 60px;
  text-align: right;
  font-size: 0.85rem;
`;

const Value = styled.span`
  color: #333333;
  font-weight: 500;
  font-size: 0.85rem;
`;

const DeleteButton = styled.button`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  color: #333333;
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:hover { background: #f5f5f5; }
`;


const DowntimeRecordsList = ({ plantId, refreshKey }) => {
  const [records, setRecords] = useState([]);
  const [maxInverters, setMaxInverters] = useState(0);

  const fetchRecords = async (preserveExisting = false) => {
    try {
      const resp = await authenticatedFetch(`/api/plants/${plantId}/inverters/records?t=${Date.now()}`, { cache: 'no-store' });
      const data = await resp.json();
      if (data && data.success && Array.isArray(data.records)) {
        setRecords(data.records);
      } else if (!preserveExisting) {
        // Only clear if we're not preserving (e.g., on initial load)
        setRecords([]);
      }
    } catch (_) { 
      // On error, only clear if not preserving
      if (!preserveExisting) {
        setRecords([]);
      }
    }
  };

  const fetchMaxInverters = async () => {
    try {
      const resp = await authenticatedFetch(`/api/plants/${plantId}/inverters/max`);
      const data = await resp.json();
      if (data && data.success) {
        setMaxInverters(Number(data.maxInverters) || 0);
      }
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { 
    fetchRecords(); 
    fetchMaxInverters();
  }, [plantId, refreshKey]);

  const parseUtcString = (utcString) => {
    if (!utcString) return null;
    const [datePart, timePart] = String(utcString).split(' ');
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm, ss] = timePart.split(':').map(Number);
    return new Date(Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0));
  };

  const formatROTime = (utcString) => {
    const dt = parseUtcString(utcString);
    if (!dt) return utcString;
    try {
      const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Bucharest',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
      });
      const parts = fmt.formatToParts(dt).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
    } catch (_) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
    }
  };

  const handleDelete = async (r) => {
    const ok = window.confirm('Delete this downtime record?');
    if (!ok) return;
    // Optimistically remove the record from the list immediately
    setRecords(prev => prev.filter(x => !(x.start_utc === r.start_utc && x.end_utc === r.end_utc)));
    try {
      const resp = await authenticatedFetch(`/api/plants/${plantId}/inverters/records?start_utc=${encodeURIComponent(r.start_utc)}&end_utc=${encodeURIComponent(r.end_utc)}`, { method: 'DELETE', cache: 'no-store' });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        // If delete failed, refresh to restore correct state
        await fetchRecords();
        return;
      }
      // Refresh records first, preserving existing list during fetch
      await fetchRecords(true);
      // Show browser alert after refresh is complete
      alert('Downtime record deleted successfully');
    } catch (e) {
      // On error, refresh to get the correct state
      await fetchRecords();
    }
  };

  return (
    <Card>
      <Title>Existing downtime records</Title>
      {records.length === 0 ? (
        <div style={{ color: '#666', opacity: 0.8, fontSize: '0.8rem' }}>No records yet.</div>
      ) : (
        <RecordsContainer>
          {records.map((r, idx) => (
            <Row key={idx}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                  <Label>Start:</Label>
                  <Value>{formatROTime(r.start_utc)}</Value>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                  <Label>End:</Label>
                  <Value>{formatROTime(r.end_utc)}</Value>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ color: '#333333', fontWeight: 600, fontSize: '0.75rem' }}>
                  {maxInverters > 0 ? (
                    <>Inverters Down: <span style={{ color: '#333333', fontWeight: 600 }}>{maxInverters - (r.inverters_running || 0)}</span></>
                  ) : (
                    <>Inverters Running: <span style={{ color: '#333333', fontWeight: 600 }}>{r.inverters_running || 0}</span></>
                  )}
                </div>
                <DeleteButton onClick={() => handleDelete(r)}>X</DeleteButton>
              </div>
            </Row>
          ))}
        </RecordsContainer>
      )}
    </Card>
  );
};

export default DowntimeRecordsList;


